import type { AnalyticsEvent, AnalyticsEventPropertyMap } from "./events";
import type { IdentifyGroupsInput, IdentifyUserInput } from "./types";

import * as Sentry from "@sentry/react";
import posthog from "posthog-js";

import { scrubEvent } from "./sanitize";

import { CONSTANTS } from "@/config/constants";
import { isMockEnabled } from "@/mock/config";

const ORGANIZATION_GROUP = "organization";
const WORKSPACE_GROUP = "workspace";

let isEnabled = false;

export const initAnalytics = (): void => {
  if (isEnabled || isMockEnabled()) return;
  if (!CONSTANTS.IS_PRODUCTION || !CONSTANTS.POSTHOG_KEY) return;

  posthog.init(CONSTANTS.POSTHOG_KEY, {
    api_host: CONSTANTS.POSTHOG_HOST,
    defaults: "2026-05-30",
    person_profiles: "identified_only",
    capture_pageview: "history_change",
    before_send: scrubEvent,
    session_recording: {
      // Passwords stay masked via posthog-js's default maskInputOptions.
      maskAllInputs: false,
    },
  });

  isEnabled = true;

  // Bridges a Sentry error to the PostHog replay of the same session. Session
  // ids rotate (inactivity, and daily), so subscribe rather than tag once -
  // otherwise every error after the first rotation points at a stale replay.
  Sentry.getCurrentScope().setTag(
    "posthog_session_id",
    posthog.get_session_id(),
  );
  posthog.onSessionId((sessionId) => {
    Sentry.getCurrentScope().setTag("posthog_session_id", sessionId);
  });
};

type TrackProperties<TEvent extends AnalyticsEvent> =
  AnalyticsEventPropertyMap[TEvent] extends undefined
    ? []
    : [properties: AnalyticsEventPropertyMap[TEvent]];

export const track = <TEvent extends AnalyticsEvent>(
  event: TEvent,
  ...properties: TrackProperties<TEvent>
): void => {
  if (!isEnabled) return;
  const [eventProperties] = properties;

  posthog.capture(event, eventProperties);
};

export const identifyUser = ({
  userId,
  clerkUserId,
  email,
  name,
  role,
}: IdentifyUserInput): void => {
  if (!isEnabled) return;
  posthog.identify(userId, { clerkUserId, email, name, role });
  Sentry.getCurrentScope().setTag("posthog_distinct_id", userId);
};

/**
 * Sticks to the person, not the event, so every later funnel can be split by it
 * without anyone adding a property to a report.
 */
export const setUserProperties = (
  properties: Record<string, string | number | boolean>,
): void => {
  if (!isEnabled) return;
  posthog.setPersonProperties(properties);
};

export const identifyGroups = ({
  organizationId,
  organizationName,
  organizationType,
  workspaceId,
  workspaceName,
}: IdentifyGroupsInput): void => {
  if (!isEnabled) return;
  posthog.group(ORGANIZATION_GROUP, organizationId, {
    name: organizationName,
    type: organizationType,
  });
  posthog.group(WORKSPACE_GROUP, workspaceId, { name: workspaceName });
};

export const resetAnalytics = (): void => {
  if (!isEnabled) return;
  posthog.reset();
};

/** Lets backend events land in the same session as the pageviews around them. */
export const getAnalyticsSessionId = (): string | null =>
  isEnabled ? posthog.get_session_id() : null;
